import type { Graph } from '@/schema/estate';

export async function createItem(
  g: Graph,
  opts: {
    estateId: string;
    itemGroupId: string;
    categoryId: string;
    name: string;
    author: string;
    groupId: string;
  },
): Promise<string> {
  const id = crypto.randomUUID();
  const now = Date.now();
  await g.item.create({
    id,
    owner_group_id: opts.groupId,
    estate_id: opts.estateId,
    item_group_id: opts.itemGroupId,
    category_id: opts.categoryId,
    inventory_number: '',
    number_aliases: [],
    name: opts.name,
    description: '',
    condition_text: '',
    provenance: '',
    dimensions: '',
    materials: '',
    status: 'captured',
    created_by: opts.author,
    created_at: now,
    updated_at: now,
    archived: false,
  });
  return id;
}

export function getItem(g: Graph, id: string) {
  const row = g.item(id)?.$data;
  return row && !row.archived ? row : undefined;
}

/** `number_aliases` arrives as a JSON string on the wire, but read paths may hand back an array. */
export function aliasesOf(row: { number_aliases: unknown }): string[] {
  const v = row.number_aliases;
  if (Array.isArray(v)) return v as string[];
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v) as unknown;
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function itemsByEstate(g: Graph, estateId: string) {
  return g.item
    .all()
    .filter((i) => i.estate_id === estateId && !i.archived)
    .sort((a, b) => a.created_at - b.created_at);
}

export function itemsByItemGroup(g: Graph, itemGroupId: string) {
  return g.item
    .all()
    .filter((i) => i.item_group_id === itemGroupId && !i.archived)
    .sort((a, b) => a.created_at - b.created_at);
}

export interface ItemBinding {
  itemGroupId: string;
}

/** Group of the most recently created item that has one. */
export function lastBinding(g: Graph, estateId: string): ItemBinding | undefined {
  const last = itemsByEstate(g, estateId)
    .reverse()
    .find((i) => i.item_group_id);
  return last ? { itemGroupId: last.item_group_id ?? '' } : undefined;
}

/** True when the user has entered nothing: no text, photos, or tags (flags are tags). */
export function isBlankItem(g: Graph, id: string): boolean {
  const row = getItem(g, id);
  if (!row) return false;
  const text = [
    row.name,
    row.description,
    row.condition_text,
    row.provenance,
    row.dimensions,
    row.materials,
  ];
  return (
    text.every((v) => !(v ?? '').trim()) &&
    !g.item_photo.all().some((p) => p.item_id === id && !p.archived) &&
    !g.item_tag.all().some((t) => t.item_id === id && !t.archived)
  );
}

export async function archiveItem(g: Graph, id: string): Promise<void> {
  await g.item.update(id, { archived: true, updated_at: Date.now() });
}

export async function updateName(g: Graph, id: string, name: string): Promise<void> {
  await g.item.update(id, { name, updated_at: Date.now() });
}

export interface EnrichmentFields {
  description: string;
  condition_text: string;
  provenance: string;
  dimensions: string;
  materials: string;
}

export async function updateEnrichment(
  g: Graph,
  id: string,
  fields: Partial<EnrichmentFields>,
): Promise<void> {
  await g.item.update(id, { ...fields, updated_at: Date.now() });
}

export async function setItemGroup(g: Graph, id: string, itemGroupId: string): Promise<void> {
  await g.item.update(id, { item_group_id: itemGroupId, updated_at: Date.now() });
}

export async function setCategory(g: Graph, id: string, categoryId: string): Promise<void> {
  await g.item.update(id, { category_id: categoryId, updated_at: Date.now() });
}
